import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CompanyFilter } from '../components/CompanyFilter';
import { Download, FileText, Loader2, TrendingDown, DollarSign, AlertTriangle, ShieldCheck, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

export default function DigestPage() {
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/companies').then(r => setCompanies(r.data)).catch(() => {}); }, []);

  const generateDigest = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCompany !== 'all') params.company_id = filterCompany;
      const res = await api.get(`/savings-agent/weekly-digest?${new URLSearchParams(params)}`);
      setDigest(res.data);
    } catch { toast.error('Failed to generate digest'); }
    finally { setLoading(false); }
  };

  const downloadDigest = () => {
    if (!digest) return;
    const content = [
      `WEEKLY COST INTELLIGENCE DIGEST`,
      `Generated: ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}`,
      ``,
      `EXECUTIVE SUMMARY`,
      digest.executive_summary || '',
      ``,
      `KEY METRICS`,
      `Total Spend: ₹${(digest.total_spend || 0).toLocaleString()}`,
      `Savings Potential: ₹${(digest.savings_potential || 0).toLocaleString()}`,
      `Best Product: ${digest.best_product || 'N/A'}`,
      `Data Quality Score: ${digest.quality_score || 0}/100`,
      ``,
      `TOP RECOMMENDATIONS`,
      ...(digest.recommendations || []).map((r, i) => `${i + 1}. ${r.title}: ${r.description}`),
      ``,
      `SAVINGS OPPORTUNITIES`,
      ...(digest.opportunities || []).map((o, i) => `${i + 1}. Switch ${o.current_product} to ${o.recommended_product} - Save ₹${o.total_extra_spend?.toLocaleString()}/period`),
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `weekly_digest_${new Date().toISOString().split('T')[0]}.txt`; a.click();
    toast.success('Digest downloaded');
  };

  return (
    <div data-testid="digest-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Weekly Cost Intelligence Digest</h1>
          <p className="text-sm text-slate-500 mt-1">AI-generated summary of key spending patterns and recommendations</p>
        </div>
        <div className="flex items-center gap-3">
          {digest && <Button variant="outline" onClick={downloadDigest} data-testid="download-digest-btn"><Download size={14} className="mr-1.5" />Download</Button>}
        </div>
      </div>

      <div className="flex gap-3 mb-8 items-end" data-testid="digest-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} className="w-56" />
        <Button onClick={generateDigest} disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="generate-digest-btn">
          {loading ? <><Loader2 size={14} className="animate-spin mr-1.5" />Generating...</> : <><FileText size={14} className="mr-1.5" />Generate Digest</>}
        </Button>
      </div>

      {!digest && !loading && (
        <Card className="stat-card text-center py-16">
          <FileText size={40} className="mx-auto text-slate-300 mb-4" />
          <p className="text-sm text-slate-500 mb-1">No digest generated yet</p>
          <p className="text-xs text-slate-400">Select a company filter and click "Generate Digest" to create the weekly report</p>
        </Card>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-slate-400" />
          <p className="ml-3 text-sm text-slate-500">Analyzing data and generating insights...</p>
        </div>
      )}

      {digest && !loading && (
        <div className="space-y-6">
          {/* KPI Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="stat-card" data-testid="digest-total-spend">
              <div className="flex items-center gap-2 mb-1"><DollarSign size={14} className="text-orange-600" /><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Spend</p></div>
              <p className="text-2xl font-bold text-slate-900 font-heading">₹{(digest.total_spend || 0).toLocaleString()}</p>
            </Card>
            <Card className="stat-card border-emerald-200" data-testid="digest-savings">
              <div className="flex items-center gap-2 mb-1"><TrendingDown size={14} className="text-emerald-600" /><p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Savings Potential</p></div>
              <p className="text-2xl font-bold text-emerald-700 font-heading">₹{(digest.savings_potential || 0).toLocaleString()}</p>
            </Card>
            <Card className="stat-card" data-testid="digest-best-product">
              <div className="flex items-center gap-2 mb-1"><BarChart3 size={14} className="text-blue-600" /><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Best Product</p></div>
              <p className="text-lg font-bold text-slate-900 font-heading">{digest.best_product || 'N/A'}</p>
              {digest.best_cph > 0 && <p className="text-xs text-slate-500">₹{digest.best_cph?.toFixed(2)}/hr</p>}
            </Card>
            <Card className="stat-card" data-testid="digest-quality">
              <div className="flex items-center gap-2 mb-1"><ShieldCheck size={14} className="text-slate-500" /><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Data Quality</p></div>
              <p className={`text-2xl font-bold font-heading ${(digest.quality_score || 0) >= 70 ? 'text-emerald-600' : 'text-amber-600'}`}>{digest.quality_score || 0}/100</p>
            </Card>
          </div>

          {/* Executive Summary */}
          {digest.executive_summary && (
            <Card className="stat-card" data-testid="digest-executive-summary">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-3">Executive Summary</h3>
              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{digest.executive_summary}</div>
            </Card>
          )}

          {/* Recommendations */}
          {(digest.recommendations || []).length > 0 && (
            <Card className="stat-card" data-testid="digest-recommendations">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-3">Key Recommendations</h3>
              <div className="space-y-3">
                {(digest.recommendations || []).map((r, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                    <div className="flex items-start gap-3">
                      <span className="text-xs font-bold text-slate-400 mt-0.5">{i + 1}</span>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{r.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{r.description}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${r.confidence === 'high' ? 'bg-emerald-50 text-emerald-600' : r.confidence === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>{r.confidence}</span>
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${r.priority === 'high' ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-400'}`}>{r.priority}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Savings Opportunities */}
          {(digest.opportunities || []).length > 0 && (
            <Card className="stat-card" data-testid="digest-opportunities">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-3">Switch Opportunities</h3>
              <div className="space-y-3">
                {(digest.opportunities || []).map((o, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-4 border border-slate-100 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{o.current_product} <span className="text-slate-400 mx-1">to</span> <span className="text-emerald-700">{o.recommended_product}</span></p>
                      <p className="text-xs text-slate-500 mt-0.5">Current: ₹{o.current_cph?.toFixed(2)}/hr vs Recommended: ₹{o.recommended_cph?.toFixed(2)}/hr</p>
                    </div>
                    <span className="text-sm font-bold text-emerald-600">Save ₹{o.total_extra_spend?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
